import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUserData } from '../../selectors/user';
import { ensureINSToken } from '../../services/interactiveServiceFetch';
import { HEARTBEAT_INTERVAL_MS } from '../../watchlist/constants';
import useVisibilityAwarePolling from '../../watchlist/hooks/useVisibilityAwarePolling';
import { listMonitors, MonitorApiError } from '../../watchlist/service';
import { IDLE_INTERVAL_MS } from '../constants';
import { setMonitors, showRunningToast, stop } from '../store';
import { countCheckingMonitors } from '../utils';

const HTTP_FORBIDDEN = 403;
const AUTH_FAILURE_STATUSES = [401, HTTP_FORBIDDEN];

const loadMonitors = async (organizationUserId, organizationId) => {
    try {
        const body = await listMonitors({ redirectOnSessionExpiry: false });
        // Cookies are shared across tabs; a successful response can belong to another account.
        if (
            Array.isArray(body?.monitors) &&
            body.monitors.some(
                monitor =>
                    monitor.owner_id !== `org_user:${organizationUserId}` || monitor.organization_id !== organizationId
            )
        ) {
            throw new MonitorApiError(HTTP_FORBIDDEN, 'Monitor account mismatch');
        }
        return { body };
    } catch (error) {
        return { error };
    }
};

const isAuthFailure = error => error instanceof MonitorApiError && AUTH_FAILURE_STATUSES.includes(error.status);

const useMonitorAlertsPoller = enabled => {
    const { organization_user_id: organizationUserId, organization_id: organizationId } =
        useSelector(getUserData) || {};
    const [pollInterval, setPollInterval] = useState(HEARTBEAT_INTERVAL_MS);
    const checkingRef = useRef(0);
    const identityReadyRef = useRef(false);
    const generationRef = useRef(0);

    useEffect(
        () => () => {
            generationRef.current += 1;
        },
        [enabled, organizationUserId, organizationId]
    );

    useEffect(() => {
        identityReadyRef.current = false;
    }, [organizationUserId, organizationId]);

    const fetchMonitors = useCallback(async () => {
        const generation = ++generationRef.current;
        const isCurrent = () => generation === generationRef.current;
        let refreshedThisPoll = false;
        // Bind to the current Django login before trusting an existing service cookie.
        if (!identityReadyRef.current) {
            const refreshResult = await ensureINSToken();
            if (!isCurrent()) return;
            if (!refreshResult.ok) {
                if (AUTH_FAILURE_STATUSES.includes(refreshResult.status)) stop();
                return;
            }
            identityReadyRef.current = true;
            refreshedThisPoll = true;
        }

        let { body, error } = await loadMonitors(organizationUserId, organizationId);
        if (!isCurrent()) return;
        // A later 403 may mean the organization changed while the page stayed open.
        if (error instanceof MonitorApiError && error.status === HTTP_FORBIDDEN && !refreshedThisPoll) {
            const refreshResult = await ensureINSToken();
            if (!isCurrent()) return;
            if (!refreshResult.ok) {
                if (AUTH_FAILURE_STATUSES.includes(refreshResult.status)) stop();
                return;
            }
            ({ body, error } = await loadMonitors(organizationUserId, organizationId));
            if (!isCurrent()) return;
        }
        if (error) {
            if (isAuthFailure(error)) stop();
            return;
        }
        const monitors = body?.monitors;
        if (!Array.isArray(monitors)) return;
        setPollInterval(monitors.length ? HEARTBEAT_INTERVAL_MS : IDLE_INTERVAL_MS);

        const checking = countCheckingMonitors(monitors);
        if (checking > 0 && checkingRef.current === 0) showRunningToast();
        checkingRef.current = checking;
        setMonitors(monitors);
    }, [organizationUserId, organizationId]);

    useVisibilityAwarePolling(
        fetchMonitors,
        pollInterval,
        enabled && organizationUserId != null && organizationId != null
    );
};

export default useMonitorAlertsPoller;
